'use client'

import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import debounce from 'lodash.debounce'
import { useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { Cart, useCart, useCartStore, useDisplayCart } from 'modules/cart'
import { useCustomerData } from 'modules/customer'
import { signUpToNewsletter } from 'modules/newsletter'
import { getQueryClient, QueryKey } from 'shared/api'
import { noop } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'

import { getSaleOrderDetails } from '../../../api'
import { useCheckoutPaymentStore } from '../../payment/store/checkoutPaymentStore'
import { PaymentMethodsNames } from '../../payment/types'
import { redirectToPaymentLink } from '../../payment/utils'
import { eventsPurchaseAnalytics } from '../analytics'
import { placeSaleOrder } from '../api'
import { getConsentValue } from '../utils'

type BlikSaleOrderError = {
  saleOrderId: string
  paymentMethodName: PaymentMethodsNames
  message?: string
}

const isBlikSaleOrderError = (err: unknown): err is BlikSaleOrderError => {
  if (typeof err !== 'object' || err === null) return false

  return (
    'saleOrderId' in err &&
    typeof (err as BlikSaleOrderError).saleOrderId === 'string' &&
    'paymentMethodName' in err &&
    (err as BlikSaleOrderError).paymentMethodName === PaymentMethodsNames.Blik
  )
}

export const usePlaceSaleOrder = () => {
  const cart = useDisplayCart()
  const storeCartId = useCart()?.id || ''
  const createCart = useCartStore(state => state.createCart)
  const setIsLoading = useCartStore(state => state.setIsLoading)
  const paymentMethod = useCheckoutPaymentStore(state => state.paymentMethodId)
  const paymentMethodName = useCheckoutPaymentStore(
    state => state.paymentMethodName
  )
  const blikCode = useCheckoutPaymentStore(state => state.blikCode)
  const marketingConsents = useCheckoutPaymentStore(
    state => state.marketingConsents
  )
  const session = useSession()
  const { user, token, update } = useCustomerData()
  const router = useRouter()

  const [isPlacingSaleOrder, setIsPlacingSaleOrder] = useState(false)

  const debouncedCreateSaleOrder = useCallback(
    debounce(
      () => {
        if (isPlacingSaleOrder) return

        setIsPlacingSaleOrder(true)
        const saveCart = cart ? { ...cart } : null

        placeSaleOrder({
          cartId: storeCartId,
          token,
          paymentMethodId: String(paymentMethod),
          blikCode,
          cancelLoading: () => {
            setIsPlacingSaleOrder(false)
          }
        })
          .finally(() => setIsLoading(false))
          .then(async resp => {
            void getQueryClient().invalidateQueries({
              queryKey: [QueryKey.profileOrders]
            })
            const order = resp?.data?.data || null

            if (order) {
              const paymentLink = order?.paymentLink || null

              await eventsPurchaseAnalytics({
                user: order.customer,
                cart: order.cart,
                paymentMethodName: paymentMethodName as string,
                order
              })

              if (token) {
                await createCart(session).then(noop).catch(noop)
              }

              try {
                // Sign in to newsletter if consent given.
                const isNewsletterSubscriber = getConsentValue(
                  marketingConsents,
                  'isNewsletterSubscriber'
                )

                if (isNewsletterSubscriber) {
                  await signUpToNewsletter({
                    email: user?.email || '',
                    formType: 'order'
                  })
                }

                // Sign in to SMS marketing if consent given.
                const isSmsConsentsGranted = getConsentValue(
                  marketingConsents,
                  'smsConsentsGranted'
                )

                if (isSmsConsentsGranted) {
                  await update({ smsConsentsGranted: true })
                }
              } catch (error) {
                reportSentryError(error, 'Error creating order')
              }

              if (paymentLink) {
                redirectToPaymentLink(paymentLink)
              } else {
                void router.push(`/podsumowanie/${order?.id}`)
              }
              setIsPlacingSaleOrder(false)
            } else {
              reportSentryError('purchase with not returning order', {
                context: JSON.stringify({
                  cartId: cart?.id,
                  paymentMethodName,
                  user
                })
              })
              void router.push(PATHS.cart)
            }
          })
          .catch(async err => {
            await getQueryClient().invalidateQueries({
              queryKey: [QueryKey.profileOrders]
            })

            if (isBlikSaleOrderError(err)) {
              try {
                const response = await getSaleOrderDetails({
                  token,
                  id: err.saleOrderId
                })

                const order = response.data.data

                await eventsPurchaseAnalytics({
                  user,
                  cart: saveCart as Cart,
                  paymentMethodName: paymentMethodName || '',
                  order
                })

                if (token) {
                  createCart(session).then(noop).catch(noop)
                }
              } catch (error) {
                reportSentryError(error, 'BlikOrderError after creating order')
              }
            } else {
              setIsPlacingSaleOrder(false)

              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
              toast.error(err.message)
              reportSentryError(err, 'Error creating order')
            }
          })
      },
      200,
      { leading: false, trailing: true }
    ),
    [
      isPlacingSaleOrder,
      setIsPlacingSaleOrder,
      storeCartId,
      setIsLoading,
      paymentMethod,
      blikCode,
      token,
      cart,
      user,
      paymentMethodName,
      router,
      createCart,
      session,
      marketingConsents,
      update
    ]
  )

  const handlePlaceSaleOrder = useCallback(() => {
    // Cancel any pending debounced calls
    debouncedCreateSaleOrder.cancel()
    setIsPlacingSaleOrder(true)
    // Then trigger a new debounced call
    debouncedCreateSaleOrder()
  }, [debouncedCreateSaleOrder])

  return { handlePlaceSaleOrder, isPlacingSaleOrder }
}
